import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlus, faStickyNote, faUser, faClock } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { getNoteColorStyle } from '../TicketNotesModal';
import TicketNotesModal from '../TicketNotesModal';
import { useTicketNotes } from '../../hooks/useTicketNotes';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }) + ' • ' + date.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const SidebarTicketNotes = ({
  ticket,
  notes: propNotes,
  isLoadingNotes: propIsLoadingNotes,
  isSavingNote: propIsSavingNote,
  createNote: propCreateNote,
  updateNote: propUpdateNote,
  deleteNote: propDeleteNote
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token } = useToken();

  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [localColors, setLocalColors] = useState({});

  const activeTicketId = ticket?.idTicket || ticket?.id;

  // Si no se pasaron props de notas, se gestionan de forma autónoma con useTicketNotes
  const hookData = useTicketNotes({
    activeTicketId: propNotes !== undefined ? null : activeTicketId,
    token
  });

  const notes = propNotes !== undefined ? propNotes : hookData.notes;
  const isLoadingNotes = propIsLoadingNotes !== undefined ? propIsLoadingNotes : hookData.isLoadingNotes;
  const isSavingNote = propIsSavingNote !== undefined ? propIsSavingNote : hookData.isSavingNote;
  const createNote = propCreateNote || hookData.createNote;
  const updateNote = propUpdateNote || hookData.updateNote;
  const deleteNote = propDeleteNote || hookData.deleteNote;

  if (!activeTicketId) return null;

  const handleOpenCreate = () => {
    setEditingNote(null);
    setShowModal(true);
  };

  const handleStartEdit = (noteItem, e) => {
    e?.stopPropagation();
    const effectiveColor = localColors[noteItem.idTicketNotas] || noteItem.noteColor;
    setEditingNote({ ...noteItem, noteColor: effectiveColor });
    setShowModal(true);
  };

  return (
    <div>
      {/* Header de la sección */}
      <div
        className="fw-semibold mb-2 pb-1 d-flex align-items-center justify-content-between"
        style={{
          fontSize: '0.68rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: isDark ? '#94a3b8' : '#64748b'
        }}
      >
        <div className="d-flex align-items-center gap-1.5">
          <span>NOTA DE TICKET</span>
          {notes.length > 0 && (
            <span
              className="badge rounded-pill"
              style={{
                backgroundColor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
                color: isDark ? '#fbbf24' : '#b45309',
                fontSize: '0.65rem',
                padding: '0.2em 0.55em',
                fontWeight: '600'
              }}
            >
              {notes.length}
            </span>
          )}
          {isLoadingNotes && (
            <FontAwesomeIcon icon={faSpinner} spin className="text-primary ms-1" style={{ fontSize: '0.7rem' }} />
          )}
        </div>

        <button
          type="button"
          onClick={handleOpenCreate}
          className="btn btn-link p-0 text-primary fw-semibold shadow-none border-0 d-flex align-items-center gap-1"
          style={{
            fontSize: '0.72rem',
            textDecoration: 'none',
            cursor: 'pointer'
          }}
          title="Agregar nueva nota"
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.65rem' }} />
          <span>Agregar</span>
        </button>
      </div>

      {/* Lista / Visualización de las notas */}
      {isLoadingNotes && notes.length === 0 ? (
        <div className="text-center py-3 text-muted" style={{ fontSize: '0.75rem' }}>
          <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-primary" />
          Cargando notas...
        </div>
      ) : notes.length === 0 ? (
        <div
          onClick={handleOpenCreate}
          className="py-3 px-3 rounded-3 border text-center transition-all"
          style={{
            fontSize: '0.75rem',
            backgroundColor: isDark ? '#0b1727' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0',
            color: isDark ? '#94a3b8' : '#64748b',
            cursor: 'pointer'
          }}
          title="Clic para agregar una nota"
        >
          <div className="d-flex align-items-center justify-content-center gap-2 py-1">
            <FontAwesomeIcon icon={faStickyNote} />
            <span>Este ticket no tiene notas.</span>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {notes.map((item) => {
            const effectiveColor = localColors[item.idTicketNotas] || item.noteColor;
            const noteColorStyle = getNoteColorStyle(effectiveColor, isDark);

            return (
              <div
                key={item.idTicketNotas}
                onClick={(e) => handleStartEdit(item, e)}
                className="rounded-3 border position-relative transition-all"
                style={{
                  ...noteColorStyle,
                  padding: '12px 14px',
                  borderWidth: '1px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  cursor: 'pointer'
                }}
                title="Clic para ver, editar o eliminar esta nota"
              >
                {/* Contenido / Cuerpo de la nota con buen espaciado */}
                <div
                  style={{
                    fontSize: '0.82rem',
                    lineHeight: '1.45',
                    color: noteColorStyle.color || (isDark ? '#f9fafd' : '#1e293b')
                  }}
                >
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {item.note}
                  </div>

                  {/* Autor y fecha formateada abajo de la nota en una sola fila */}
                  <div
                    className="d-flex align-items-center justify-content-between text-nowrap mt-2 pt-1.5 border-top"
                    style={{
                      fontSize: '0.71rem',
                      opacity: 0.82,
                      borderColor: 'rgba(0, 0, 0, 0.08)'
                    }}
                  >
                    <span className="d-inline-flex align-items-center gap-1 fw-semibold text-truncate me-2" title={item.createdUser || 'Asesor'}>
                      <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.62rem', opacity: 0.75 }} />
                      <span className="text-truncate">{item.createdUser || 'Asesor'}</span>
                    </span>
                    <span className="d-inline-flex align-items-center gap-1 flex-shrink-0" style={{ fontSize: '0.69rem' }}>
                      <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.62rem', opacity: 0.75 }} />
                      <span>{formatDateTime(item.createdDate)}</span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal para Creación / Edición de Nota */}
      {showModal && (
        <TicketNotesModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingNote(null);
          }}
          ticket={ticket}
          noteToEdit={editingNote}
          isSavingNote={isSavingNote}
          createNote={createNote}
          updateNote={async (payload) => {
            if (payload?.idTicketNotas && payload?.noteColor) {
              setLocalColors(prev => ({ ...prev, [payload.idTicketNotas]: payload.noteColor }));
            }
            return await updateNote(payload);
          }}
          deleteNote={async (idTicketNotas) => {
            return await deleteNote({ idTicketNotas });
          }}
        />
      )}
    </div>
  );
};

SidebarTicketNotes.propTypes = {
  ticket: PropTypes.object.isRequired,
  notes: PropTypes.array,
  isLoadingNotes: PropTypes.bool,
  isSavingNote: PropTypes.bool,
  createNote: PropTypes.func,
  updateNote: PropTypes.func,
  deleteNote: PropTypes.func
};

export default SidebarTicketNotes;
